/**
 * RiskScoreVisualizer Component
 * 
 * Displays a gauge chart showing the combined risk score
 * with segments for Approve (0-30), Review (30-70), Block (70-100)
 */

import React, { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { cn } from '../../lib/utils';
import type { RiskScoreBreakdown } from '../../types/fraud';

export interface RiskScoreVisualizerProps {
  /** Risk score breakdown data */
  breakdown: RiskScoreBreakdown;
  /** Size of the gauge */
  size?: 'sm' | 'md' | 'lg';
  /** Show detailed breakdown tooltip */
  showTooltip?: boolean;
  /** Additional CSS classes */
  className?: string;
}

interface GaugeSegment {
  name: string;
  value: number;
  color: string;
  startAngle: number;
  endAngle: number;
}

const sizeConfig = {
  sm: { width: 120, height: 80, fontSize: 'text-lg', strokeWidth: 8 },
  md: { width: 200, height: 120, fontSize: 'text-2xl', strokeWidth: 12 },
  lg: { width: 280, height: 160, fontSize: 'text-4xl', strokeWidth: 16 },
};

/**
 * Calculate gauge segments based on score
 */
function calculateGaugeSegments(_score: number): GaugeSegment[] {
  // Define segments
  const segments: GaugeSegment[] = [
    { name: 'Approve', value: 30, color: '#10B981', startAngle: 180, endAngle: 126 },
    { name: 'Review', value: 40, color: '#F59E0B', startAngle: 126, endAngle: 54 },
    { name: 'Block', value: 30, color: '#EF4444', startAngle: 54, endAngle: 0 },
  ];

  return segments;
}

/**
 * Get needle angle based on score
 */
function getNeedleAngle(score: number): number {
  const normalizedScore = Math.min(100, Math.max(0, score * 100));
  // Map 0-100 to 180-0 degrees
  return 180 - (normalizedScore * 1.8);
}

/**
 * Get color for score value
 */
function getScoreColor(score: number): string {
  if (score <= 0.3) return '#10B981'; // Green
  if (score <= 0.7) return '#F59E0B'; // Yellow/Orange
  return '#EF4444'; // Red
}

/**
 * Get risk level label
 */
function getRiskLabel(score: number): string {
  if (score <= 0.3) return 'Low Risk';
  if (score <= 0.5) return 'Medium Risk';
  if (score <= 0.7) return 'High Risk';
  return 'Critical Risk';
}

/**
 * RiskScoreVisualizer - Gauge chart for risk scores
 * 
 * @example
 * <RiskScoreVisualizer
 *   breakdown={{
 *     combined: 0.75,
 *     qbScore: 0.60,
 *     vendorVerificationScore: 0.40,
 *     components: [...]
 *   }}
 *   size="lg"
 * />
 */
export const RiskScoreVisualizer: React.FC<RiskScoreVisualizerProps> = ({
  breakdown,
  size = 'md',
  showTooltip = true,
  className,
}) => {
  const [hoveredComponent, setHoveredComponent] = useState<string | null>(null);
  const config = sizeConfig[size];
  const segments = calculateGaugeSegments(breakdown.combined);
  const needleAngle = getNeedleAngle(breakdown.combined);
  const scoreColor = getScoreColor(breakdown.combined);
  const riskLabel = getRiskLabel(breakdown.combined);

  // Custom tooltip for the gauge
  const CustomTooltip = ({ active }: { active?: boolean }) => {
    if (!active || !showTooltip) return null;

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 max-w-xs">
        <p className="font-semibold text-gray-900 mb-2">Risk Score Breakdown</p>
        <div className="space-y-1">
          {breakdown.components.map((component) => (
            <div key={component.name} className="flex justify-between text-sm">
              <span className="text-gray-600">{component.name}</span>
              <span className="font-medium">
                {(component.contribution * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex justify-between text-sm font-medium">
            <span>Total</span>
            <span style={{ color: scoreColor }}>
              {(breakdown.combined * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn('flex flex-col items-center', className)}>
      {/* Gauge Container */}
      <div className="relative" style={{ width: config.width, height: config.height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Background segments */}
            <Pie
              data={segments}
              cx="50%"
              cy="100%"
              startAngle={180}
              endAngle={0}
              innerRadius={config.width * 0.35}
              outerRadius={config.width * 0.45}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {segments.map((segment, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={segment.color}
                  fillOpacity={hoveredComponent === segment.name ? 1 : 0.8}
                  onMouseEnter={() => setHoveredComponent(segment.name)}
                  onMouseLeave={() => setHoveredComponent(null)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </Pie>
            
            {/* Needle position indicator (small arc) */}
            <Pie
              data={[{ value: 100 }]}
              cx="50%"
              cy="100%"
              startAngle={needleAngle - 2}
              endAngle={needleAngle + 2}
              innerRadius={0}
              outerRadius={config.width * 0.48}
              fill={scoreColor}
              stroke="none"
            />

            <RechartsTooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Needle */}
        <svg
          className="absolute inset-0 pointer-events-none"
          viewBox={`0 0 ${config.width} ${config.height}`}
        >
          {/* Needle line */}
          <line
            x1={config.width / 2}
            y1={config.height}
            x2={config.width / 2 + Math.cos((needleAngle - 90) * (Math.PI / 180)) * (config.width * 0.4)}
            y2={config.height + Math.sin((needleAngle - 90) * (Math.PI / 180)) * (config.width * 0.4)}
            stroke="#374151"
            strokeWidth={3}
            strokeLinecap="round"
          />
          {/* Needle center circle */}
          <circle
            cx={config.width / 2}
            cy={config.height}
            r={6}
            fill="#374151"
          />
        </svg>

        {/* Score display */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/4 text-center">
          <span
            className={cn('font-bold', config.fontSize)}
            style={{ color: scoreColor }}
          >
            {(breakdown.combined * 100).toFixed(0)}
          </span>
          <span className="text-sm text-gray-400 ml-0.5">%</span>
        </div>
      </div>

      {/* Risk Label */}
      <div className="mt-6 text-center">
        <span
          className={cn(
            'inline-block px-3 py-1 rounded-full text-sm font-medium',
            breakdown.combined <= 0.3 && 'bg-green-100 text-green-800',
            breakdown.combined > 0.3 && breakdown.combined <= 0.5 && 'bg-yellow-100 text-yellow-800',
            breakdown.combined > 0.5 && breakdown.combined <= 0.7 && 'bg-orange-100 text-orange-800',
            breakdown.combined > 0.7 && 'bg-red-100 text-red-800'
          )}
        >
          {riskLabel}
        </span>
      </div>

      {/* Component Breakdown */}
      {size !== 'sm' && (
        <div className="mt-6 w-full space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">
            Score Components
          </p>
          {breakdown.components.map((component) => (
            <div
              key={component.name}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{
                    backgroundColor:
                      component.score <= 0.3
                        ? '#10B981'
                        : component.score <= 0.7
                        ? '#F59E0B'
                        : '#EF4444',
                  }}
                />
                <span className="text-gray-600">{component.name}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${component.score * 100}%`,
                      backgroundColor:
                        component.score <= 0.3
                          ? '#10B981'
                          : component.score <= 0.7
                          ? '#F59E0B'
                          : '#EF4444',
                    }}
                  />
                </div>
                <span className="font-medium w-10 text-right">
                  {(component.contribution * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mr-1.5" />
          <span className="text-gray-600">Approve (0-30)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1.5" />
          <span className="text-gray-600">Review (30-70)</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-red-500 mr-1.5" />
          <span className="text-gray-600">Block (70-100)</span>
        </div>
      </div>
    </div>
  );
};

export default RiskScoreVisualizer;
